import React, { useState, useEffect } from 'react';
import { myAxios } from '../services/Helper';
import './admin.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faUserShield, faEnvelope, faPhone, faCalendarAlt, faEdit, faTrash } from '@fortawesome/free-solid-svg-icons';

const ManageUsers = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        role: 'ROLE_USER'
    });
    const [message, setMessage] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            // Adjust endpoint as needed based on your API
            const response = await myAxios.get('/api/admin/users');
            setUsers(response.data);
            setLoading(false);
        } catch (err) {
            setError(err.message || 'Failed to fetch users');
            setLoading(false);
        }
    };

    const handleFormChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleEditUser = (user) => {
        setSelectedUser(user);
        setFormData({
            email: user.email,
            role: user.role
        });
        setShowForm(true);
    };

    const handleUpdateRole = async (e) => {
        e.preventDefault();
        setMessage(null);

        try {
            await myAxios.put('/api/admin/change-role', formData);
            setMessage({ type: 'success', text: 'User role updated successfully!' });
            setShowForm(false);
            fetchUsers(); // Refresh user list
        } catch (error) {
            setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to update user role' });
        }
    };

    const handleDeleteUser = async (userId) => {
        if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
            try {
                // Adjust endpoint as needed based on your API
                await myAxios.delete(`/api/admin/users/${userId}`);
                fetchUsers(); // Refresh user list
            } catch (err) {
                setError(err.message || 'Failed to delete user');
            }
        }
    };

    const getRoleBadgeClass = (role) => {
        switch (role) {
            case 'ROLE_ADMIN':
                return 'role-badge admin';
            case 'ROLE_SUBADMIN':
                return 'role-badge subadmin';
            default:
                return 'role-badge user';
        }
    };

    const getRoleDisplayName = (role) => {
        switch (role) {
            case 'ROLE_ADMIN':
                return 'Admin';
            case 'ROLE_SUBADMIN':
                return 'Sub Admin';
            default:
                return 'User';
        }
    };

    if (loading) return <div className="loading-container"><div className="loading">Loading users...</div></div>;
    if (error) return <div className="error-container"><div className="error">Error: {error}</div></div>;

    return (
        <div className="users-container">
            <div className="users-header">
                <h2>Manage Users</h2>
                <button
                    className="change-role-btn"
                    onClick={() => {
                        setSelectedUser(null);
                        setFormData({ email: '', role: 'ROLE_USER' });
                        setShowForm(true);
                    }}
                >
                    <FontAwesomeIcon icon={faUserShield} /> Change User Role
                </button>
            </div>

            {message && (
                <div className={`message ${message.type}`}>
                    {message.text}
                </div>
            )}

            {showForm && (
                <div className="form-overlay">
                    <div className="form-container">
                        <h3>{selectedUser ? 'Edit User Role' : 'Change User Role'}</h3>
                        <form onSubmit={handleUpdateRole}>
                            <div className="form-group">
                                <label htmlFor="email">User Email</label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleFormChange}
                                    required
                                    placeholder="Enter user email"
                                    className="form-input"
                                    disabled={selectedUser !== null}
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="role">Role</label>
                                <select
                                    id="role"
                                    name="role"
                                    value={formData.role}
                                    onChange={handleFormChange}
                                    required
                                    className="form-select"
                                >
                                    <option value="ROLE_USER">User</option>
                                    <option value="ROLE_SUBADMIN">Sub Admin</option>
                                    <option value="ROLE_ADMIN">Admin</option>
                                </select>
                            </div>

                            <div className="form-buttons">
                                <button type="submit" className="btn-primary">
                                    Update Role
                                </button>
                                <button
                                    type="button"
                                    className="btn-secondary"
                                    onClick={() => setShowForm(false)}
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <div className="users-grid">
                {users.length === 0 ? (
                    <div className="no-data">No users found</div>
                ) : (
                    users.map((user) => (
                        <div key={user.id} className="user-card">
                            <div className="user-header">
                                <h3>
                                    <FontAwesomeIcon icon={faUser} className="user-icon" />
                                    {user.firstName} {user.lastName}
                                </h3>
                                <span className={getRoleBadgeClass(user.role)}>
                                    {getRoleDisplayName(user.role)}
                                </span>
                            </div>

                            <div className="user-body">
                                <p>
                                    <FontAwesomeIcon icon={faEnvelope} className="user-info-icon" />
                                    <span>{user.email}</span>
                                </p>
                                {user.phone && (
                                    <p>
                                        <FontAwesomeIcon icon={faPhone} className="user-info-icon" />
                                        <span>{user.phone}</span>
                                    </p>
                                )}
                                <p>
                                    <FontAwesomeIcon icon={faCalendarAlt} className="user-info-icon" />
                                    <span>Joined: {new Date(user.createdAt).toLocaleDateString()}</span>
                                </p>
                            </div>

                            <div className="user-footer">
                                <button
                                    className="edit-btn action-btn"
                                    onClick={() => handleEditUser(user)}
                                    title="Edit Role"
                                >
                                    <FontAwesomeIcon icon={faEdit} />
                                </button>
                                <button
                                    className="delete-btn action-btn"
                                    onClick={() => handleDeleteUser(user.id)}
                                    title="Delete User"
                                >
                                    <FontAwesomeIcon icon={faTrash} />
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default ManageUsers; 